import { useMemo, useState } from 'react';
import { buildHistoryRequestRows, filterHistoryRows, buildHistoryYearOptions, countHistoryRows } from '../utils/vacationHistoryReport';
import { allocateHistoryPeriods } from '../utils/vacationHistoryPeriods';
import { useVacationHistoryDataQuery } from './data/useVacationHistoryDataQuery';
import type { HistoryEventKind, HistoryReportCounts, HistoryRequestRow } from '../models/vacationHistoryReport.types';

interface FilterState {
    kind: HistoryEventKind;
    month: number | null;
    year: number | null;
    search: string;
}

const getInitialFilterState = (): FilterState => {
    const today = new Date();
    return {
        kind: 'requested',
        month: today.getMonth() + 1,
        year: today.getFullYear(),
        search: '',
    };
};

export function useVacationHistoryReport() {
    const [state, setState] = useState<FilterState>(getInitialFilterState());

    const { requests, collaborators, collaboratorMaps, balancesById, isLoading } = useVacationHistoryDataQuery();

    const allRows = useMemo(() => {
        const rows = buildHistoryRequestRows(requests, collaborators, collaboratorMaps);
        return allocateHistoryPeriods(rows, balancesById);
    }, [requests, collaborators, collaboratorMaps, balancesById]);

    const periodFilterKey = useMemo(() => {
        if (state.year === null) return '';
        if (state.month === null) return String(state.year);
        return `${state.year}-${String(state.month).padStart(2, '0')}`;
    }, [state.month, state.year]);

    const rows = useMemo(
        () =>
            filterHistoryRows(allRows, {
                kind: state.kind,
                periodKey: periodFilterKey,
                search: state.search,
            }),
        [allRows, state.kind, periodFilterKey, state.search]
    );

    const counts: HistoryReportCounts = useMemo(() => countHistoryRows(rows), [rows]);

    const yearOptions = useMemo(() => buildHistoryYearOptions(allRows), [allRows]);

    const findRequest = useMemo(() => (requestId: string | null): HistoryRequestRow | undefined => {
        if (!requestId) return undefined;
        return allRows.find((r) => r.requestId === requestId);
    }, [allRows]);

    const findVacationRequest = useMemo(() => (requestId: string | null) => {
        if (!requestId) return undefined;
        return requests.find((r) => r.id === requestId);
    }, [requests]);

    return {
        rows,
        allRows,
        counts,
        yearOptions,
        isLoading,
        kind: state.kind,
        setKind: (newKind: HistoryEventKind) =>
            setState((prev) => ({ ...prev, kind: newKind })),
        selectedMonth: state.month,
        setSelectedMonth: (month: number | null) =>
            setState((prev) => ({ ...prev, month })),
        selectedYear: state.year,
        setSelectedYear: (year: number | null) =>
            setState((prev) => ({ ...prev, year })),
        search: state.search,
        setSearch: (search: string) =>
            setState((prev) => ({ ...prev, search })),
        periodFilterKey: periodFilterKey,
        findRequest,
        findVacationRequest,
    };
}
